import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { createDraftCatalog, updateDraftCatalog } from '../../common/fetch';
import { deviceListSavedAsDraftMsg, pcsMsg, savingTheDataFailedMsg, selectAllMsg, t } from '../../common/i18n';
import { addNotification } from '../../public/site/notificationStore.js';
import type { Catalog } from '../../generated/api/catalog';
import type { VirtualCatalog } from '../../generated/api/virtualCatalog';

interface EditDiscontinuedDevicesProps {
  virtualCatalog: VirtualCatalog;
  catalog: Catalog;
  mdmId: string;
}

interface DiscontinuedDeviceItemProps {
  deviceName: string;
  checked: boolean;
  onSelectDevice: () => void;
  disabled: boolean;
}

const DiscontinuedDeviceItem = ({ deviceName, checked, onSelectDevice, disabled }: DiscontinuedDeviceItemProps) => (
  <>
    <CL.Checkbox
      key={`discontinuedDevice.${deviceName}`}
      label={deviceName}
      checked={checked}
      onClick={onSelectDevice}
      disabled={disabled}
    />
    <CL.Badge color="orange" text={t.KNBW('Removed from selection')} />
  </>
);

export const EditDiscontinuedDevices = ({ catalog, virtualCatalog, mdmId }: EditDiscontinuedDevicesProps) => {
  const navigate = useNavigate();
  const [selectedDiscontinuedDevices, setSelectedDiscontinuedDevices] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const currentDiscontinuedDevices = catalog.discontinuedProducts ?? [];

  const allSelected = selectedDiscontinuedDevices.length === currentDiscontinuedDevices.length;

  const onSelectAll = () =>
    allSelected
      ? setSelectedDiscontinuedDevices([])
      : setSelectedDiscontinuedDevices(currentDiscontinuedDevices.map(d => d.onlineModelCode));

  const onSelectDevice = (onlineModelCode: string) =>
    setSelectedDiscontinuedDevices(
      selectedDiscontinuedDevices.includes(onlineModelCode)
        ? selectedDiscontinuedDevices.filter(name => name !== onlineModelCode)
        : [...selectedDiscontinuedDevices, onlineModelCode]
    );

  const onRemove = async () => {
    setIsSaving(true);
    const updatedProducts = catalog.products.filter(product => !selectedDiscontinuedDevices.includes(product));
    const updatedCatalog = {
      ...catalog,
      products: updatedProducts,
    };
    try {
      // If we already have a draft, we update it, otherwise we create a new draft
      if (virtualCatalog.draft) {
        await updateDraftCatalog(virtualCatalog.virtualCatalogCode, updatedCatalog, mdmId);
      } else {
        await createDraftCatalog(virtualCatalog.virtualCatalogCode, updatedCatalog, mdmId);
      }
      setSelectedDiscontinuedDevices([]);
      addNotification(t.P7JV(deviceListSavedAsDraftMsg));
      navigate(
        `${generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: virtualCatalog.virtualCatalogCode })}?mdmId=${mdmId}`
      );
    } catch {
      addNotification(t.RQA1(savingTheDataFailedMsg), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (currentDiscontinuedDevices.length === 0) {
    return null;
  } else {
    return (
      <div className={`${dsClass.PADDING_TOP_1} ${dsClass.PADDING_BOTTOM_8}`}>
        <CL.Description
          columns={1}
          items={[
            {
              title: `${t.GL4B('Products removed from selection')} (${currentDiscontinuedDevices.length} ${t.B3MG(pcsMsg)})`,
              description: `${t.HR7X('The products removed from selection are no longer available, so they are not shown on employees’ device lists. You can see the products until you remove them on this page or the Add or remove devices page.')}`,
            },
          ]}
        />

        <div className={`${dsClass.PADDING_BOTTOM_1}`}>
          <strong>{t.E6PL('Remove products')}</strong>
        </div>

        <div className="of-catalog-details__discontinued-devices__select-all">
          <CL.Checkbox
            label={t.A8VA(selectAllMsg)}
            onClick={onSelectAll}
            key={`allSelected.${allSelected}}`}
            checked={allSelected}
            disabled={isSaving}
          />
        </div>
        <div className="of-catalog-details__discontinued-devices__list">
          {currentDiscontinuedDevices.map(device => (
            <div className="of-catalog-details__discontinued-devices__list_item" key={device.onlineModelName}>
              <DiscontinuedDeviceItem
                key={`discontinuedDevice.${device.onlineModelName}.${allSelected}`}
                deviceName={device.onlineModelName}
                checked={selectedDiscontinuedDevices.includes(device.onlineModelCode)}
                onSelectDevice={() => onSelectDevice(device.onlineModelCode)}
                disabled={isSaving}
              />
            </div>
          ))}
        </div>

        <div className={`${dsClass.PADDING_TOP_3}`}>
          <CL.Button
            onClick={onRemove}
            loading={isSaving}
            disabled={isSaving || selectedDiscontinuedDevices.length === 0}
          >
            {t.X05S('Remove from the list')}
          </CL.Button>
        </div>
      </div>
    );
  }
};
